import React, { useMemo, useRef } from "react";
import { Box } from "@mui/material";

// --- HSV ↔ HEX ---
const clamp = (n, a, b) => Math.max(a, Math.min(b, n));

const hexToHsv = (hex) => {
  let h = (hex || "").replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return { h: 0, s: 0, v: 0 };
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const d = max - min;
  let hue = 0;
  if (d !== 0) {
    if (max === r) hue = ((g - b) / d) % 6;
    else if (max === g) hue = (b - r) / d + 2;
    else hue = (r - g) / d + 4;
    hue *= 60;
    if (hue < 0) hue += 360;
  }
  return { h: hue, s: max === 0 ? 0 : d / max, v: max };
};

const hsvToHex = (h, s, v) => {
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;
  let r = 0, g = 0, b = 0;
  if (h < 60) [r, g, b] = [c, x, 0];
  else if (h < 120) [r, g, b] = [x, c, 0];
  else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c];
  else if (h < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  const to = (n) => Math.round((n + m) * 255).toString(16).padStart(2, "0");
  return `#${to(r)}${to(g)}${to(b)}`;
};

// Інлайн-колірпікер: квадрат насиченості/яскравості (тягнеш кружок) + повзунок
// відтінку. onChange викликається в реальному часі під час перетягування —
// зовнішнє прев'ю оновлюється миттєво; збереження робить кнопка форми.
export default function ColorPicker({ value, onChange, disabled }) {
  const { h, s, v } = useMemo(() => hexToHsv(value), [value]);
  const svRef = useRef(null);
  const hueRef = useRef(null);
  const dragRef = useRef(null); // 'sv' | 'hue' | null

  const setFromSV = (clientX, clientY) => {
    const r = svRef.current.getBoundingClientRect();
    const ns = clamp((clientX - r.left) / r.width, 0, 1);
    const nv = clamp(1 - (clientY - r.top) / r.height, 0, 1);
    onChange(hsvToHex(h, ns, nv));
  };
  const setFromHue = (clientX) => {
    const r = hueRef.current.getBoundingClientRect();
    const nh = clamp((clientX - r.left) / r.width, 0, 1) * 360;
    onChange(hsvToHex(nh, s || 1, v || 1));
  };

  const startSV = (e) => {
    if (disabled) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = "sv";
    setFromSV(e.clientX, e.clientY);
  };
  const startHue = (e) => {
    if (disabled) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = "hue";
    setFromHue(e.clientX);
  };
  const move = (e) => {
    if (dragRef.current === "sv") setFromSV(e.clientX, e.clientY);
    else if (dragRef.current === "hue") setFromHue(e.clientX);
  };
  const end = () => { dragRef.current = null; };

  return (
    <Box sx={{ width: 232, userSelect: "none" }}>
      {/* Квадрат насиченості / яскравості */}
      <Box
        ref={svRef}
        onPointerDown={startSV}
        onPointerMove={move}
        onPointerUp={end}
        sx={{
          position: "relative", width: "100%", height: 150, borderRadius: 1.5,
          cursor: disabled ? "default" : "crosshair", touchAction: "none",
          background:
            `linear-gradient(to top, #000, rgba(0,0,0,0)), ` +
            `linear-gradient(to right, #fff, hsl(${h}, 100%, 50%))`,
        }}
      >
        <Box sx={{
          position: "absolute", left: `${s * 100}%`, top: `${(1 - v) * 100}%`,
          width: 16, height: 16, transform: "translate(-50%,-50%)",
          borderRadius: "50%", border: "2px solid #fff", boxShadow: "0 0 0 1px rgba(0,0,0,0.4)",
          bgcolor: value, pointerEvents: "none",
        }} />
      </Box>

      {/* Повзунок відтінку */}
      <Box
        ref={hueRef}
        onPointerDown={startHue}
        onPointerMove={move}
        onPointerUp={end}
        sx={{
          position: "relative", width: "100%", height: 16, mt: 1.5, borderRadius: 8,
          cursor: disabled ? "default" : "pointer", touchAction: "none",
          background: "linear-gradient(to right, #f00 0%, #ff0 17%, #0f0 33%, #0ff 50%, #00f 67%, #f0f 83%, #f00 100%)",
        }}
      >
        <Box sx={{
          position: "absolute", left: `${(h / 360) * 100}%`, top: "50%",
          width: 18, height: 18, transform: "translate(-50%,-50%)",
          borderRadius: "50%", border: "2px solid #fff", boxShadow: "0 0 0 1px rgba(0,0,0,0.4)",
          bgcolor: `hsl(${h},100%,50%)`, pointerEvents: "none",
        }} />
      </Box>
    </Box>
  );
}
